import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Instagram, Youtube, MessageSquare, Globe, Mail, Shield, FileText, Facebook, Music, Smartphone } from 'lucide-react';
import ContactModal from './ContactModal';

interface FooterProps {
  onPageChange: (page: string) => void;
}

export default function Footer({ onPageChange }: FooterProps) {
  const navigate = useNavigate();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  const socialLinks = {
    instagram: 'https://www.instagram.com/scavenjer.game/',
    twitter: 'https://x.com/ScavenjerGame',
    youtube: 'https://www.youtube.com/@ScavenjerZaidek',
    discord: 'https://discord.gg/wX9hjx4cwA',
    facebook: 'https://www.facebook.com/scavenjergame',
    tiktok: 'https://www.tiktok.com/@scavenjergame'
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    onPageChange(path.replace('/', ''));
  };

  return (
    <footer className="relative mt-auto bg-[#0A0A0A] border-t border-[#2DD4BF]/20">
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#2DD4BF]/5 to-[#EC4899]/5 pointer-events-none" />

      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img
                src="https://ik.imagekit.io/q9x52ygvo/Untitled.png?updatedAt=1731900408675"
                alt="Scavenjer Logo"
                className="w-10 h-10 object-contain"
              />
              <span className="text-xl font-bold bg-gradient-to-r from-[#2DD4BF] to-[#EC4899] bg-clip-text text-transparent">
                SCAVENJER
              </span>
            </div>
            <p className="text-gray-400 text-sm">
              Revolutionizing the gaming landscape through augmented reality and blockchain technology.
            </p>
            <div className="flex gap-4">
              <a
                href={socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#2DD4BF] transition-colors"
              >
                <Instagram size={20} />
              </a>
              {/* X (formerly Twitter) */}
              <a
                href={socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#2DD4BF] transition-colors"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4l11.733 16h4.267l-11.733 -16z" />
                  <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" />
                </svg>
              </a>
              <a
                href={socialLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#2DD4BF] transition-colors"
              >
                <Youtube size={20} />
              </a>
              <a
                href={socialLinks.discord}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#2DD4BF] transition-colors"
              >
                <MessageSquare size={20} />
              </a>
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#2DD4BF] transition-colors"
              >
                <Facebook size={20} />
              </a>
              <a
                href={socialLinks.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#2DD4BF] transition-colors"
              >
                <Music size={20} />
              </a>
            </div>
          </div>

          {/* Explore Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Explore</h3>
            <ul className="space-y-3">
              <li><Link to="/roadmap" className="footer-link">Roadmap</Link></li>
              <li><Link to="/whitepaper" className="footer-link">Whitepaper</Link></li>
              {/* Planned release: Simulations footer link preserved off-menu.
              <li><a href="https://simulations-gamma.vercel.app/" target="_blank" rel="noopener noreferrer" className="footer-link">Simulations</a></li>
              */}
            </ul>
          </div>

          {/* Community Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Community</h3>
            <ul className="space-y-3">
                <li><Link to="/hosts" className="footer-link">Explore Hosts</Link></li>
                <li><Link to="/leaderboard" className="footer-link">Leaderboard</Link></li>
                <li><Link to="/host-application" className="footer-link">Become a Host</Link></li>
                <li><Link to="/events" className="footer-link">Events</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/privacy" className="footer-link flex items-center gap-2">
                  <Shield size={16} /> Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="footer-link flex items-center gap-2">
                  <FileText size={16} /> Terms & Conditions
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-6 border-t border-[#2DD4BF]/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Scavenjer. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="text-gray-400 text-sm flex items-center gap-2">
                <Smartphone size={16} className="text-[#2DD4BF]" />
                <span className="text-xs md:text-sm">
                  Available as mobile app - Add to home screen on your device
                </span>
              </div>
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="text-gray-400 hover:text-[#2DD4BF] transition-colors text-sm flex items-center gap-2"
              >
                <Mail size={16} />
                Contact
              </button>
              <a
                href="https://app.marblever.se/i/3MU6Wfa"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#2DD4BF] transition-colors text-sm flex items-center gap-2"
              >
                <Globe size={16} />
                Marbleverse
              </a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Contact Modal */}
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </footer>
  );
}
